import * as React from 'react'
import {
  useWebcardReader,
  WebcardStatus,
} from '../hooks/useWebcardReader'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

// ─── OS detection ─────────────────────────────────────────────────────────────

const getInstallerUrl = (t: (key: string) => string): { url: string; label: string } => {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('win')) return { url: '/assets/webcard.msi', label: t('importModule.webcard-download-windows') }
  return { url: '/assets/webcard.pkg', label: t('importModule.webcard-download-macos') }
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

const getStatusConfig = (t: (key: string) => string): Record<
  WebcardStatus,
  { label: string; dot: string; bg: string; text: string }
> => ({
  'no-extension': {
    label: t('importModule.webcard-no-extension'),
    dot: 'tw-bg-red-500',
    bg: 'tw-bg-red-50 tw-border-red-200',
    text: 'tw-text-red-700',
  },
  'ws-connecting': {
    label: t('importModule.webcard-connecting'),
    dot: 'tw-bg-blue-400 tw-animate-pulse',
    bg: 'tw-bg-blue-50 tw-border-blue-200',
    text: 'tw-text-blue-700',
  },
  'ws-unavailable': {
    label: t('importModule.webcard-unavailable'),
    dot: 'tw-bg-red-500',
    bg: 'tw-bg-red-50 tw-border-red-200',
    text: 'tw-text-red-700',
  },
  'no-reader': {
    label: t('importModule.webcard-no-reader'),
    dot: 'tw-bg-slate-400',
    bg: 'tw-bg-slate-50 tw-border-slate-200',
    text: 'tw-text-slate-600',
  },
  idle: {
    label: t('importModule.webcard-insert-card'),
    dot: 'tw-bg-slate-400',
    bg: 'tw-bg-slate-50 tw-border-slate-200',
    text: 'tw-text-slate-600',
  },
  'card-detected': {
    label: t('importModule.webcard-card-detected'),
    dot: 'tw-bg-green-500',
    bg: 'tw-bg-green-50 tw-border-green-200',
    text: 'tw-text-green-700',
  },
  reading: {
    label: t('importModule.webcard-reading'),
    dot: 'tw-bg-blue-500 tw-animate-pulse',
    bg: 'tw-bg-blue-50 tw-border-blue-200',
    text: 'tw-text-blue-700',
  },
  done: {
    label: t('importModule.webcard-done'),
    dot: 'tw-bg-green-500',
    bg: 'tw-bg-green-50 tw-border-green-200',
    text: 'tw-text-green-700',
  },
  error: {
    label: t('importModule.webcard-error'),
    dot: 'tw-bg-red-500',
    bg: 'tw-bg-red-50 tw-border-red-200',
    text: 'tw-text-red-700',
  },
})

const StatusBadge = ({ status, t }: { status: WebcardStatus; t: (key: string) => string }) => {
  const cfg = getStatusConfig(t)[status]
  return (
    <div
      className={`tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-px-4 tw-py-3 ${cfg.bg} ${cfg.text}`}
    >
      <span className={`tw-h-2.5 tw-w-2.5 tw-flex-shrink-0 tw-rounded-full ${cfg.dot}`} />
      <span className="tw-text-sm tw-font-medium">{cfg.label}</span>
    </div>
  )
}

// ─── No-extension panel ───────────────────────────────────────────────────────

const NoExtensionPanel = ({ t }: { t: (key: string) => string }) => {
  const { url, label } = getInstallerUrl(t)
  return (
    <div className="tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-4 tw-text-sm tw-text-amber-800">
      <p className="tw-mb-3 tw-font-semibold">
        {t('importModule.webcard-install-prompt')}
      </p>
      <a
        href={url}
        download
        className="tw-inline-block tw-rounded-md tw-bg-amber-600 tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-transition hover:tw-bg-amber-700"
      >
        {label}
      </a>
      <p className="tw-mt-3 tw-text-xs tw-text-amber-700">
        {t('importModule.webcard-after-install')}
      </p>
    </div>
  )
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

const ReadingProgressBar = ({ progress, t }: { progress: number; t: (key: string) => string }) => (
  <div className="tw-space-y-1.5">
    <div className="tw-flex tw-justify-between tw-text-xs tw-text-slate-500">
      <span>{t('importModule.webcard-read-progress')}</span>
      <span>{progress}%</span>
    </div>
    <div className="tw-h-3 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-slate-200">
      <div
        className="tw-h-full tw-rounded-full tw-bg-blue-500 tw-transition-all tw-duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
)

// ─── Main component ───────────────────────────────────────────────────────────

type Props = {
  onDddReady: (dddFile: File) => void
}

export const PopupStepWebcard = ({ onDddReady }: Props) => {
  const { t } = useLocalize()
  const { status, progress, dddData, readCard } = useWebcardReader()
  const [isReading, setIsReading] = React.useState(false)

  const isDone = status === 'done' && dddData

  const canRead =
    !isDone &&
    (status === 'card-detected' || status === 'idle' || status === 'error')

  const handleRead = async () => {
    setIsReading(true)
    await readCard()
    setIsReading(false)
  }

  const handleImport = () => {
    if (!dddData) return
    const filename = `tachograph_${new Date().toISOString().slice(0, 10)}.ddd`
    const file = new File([dddData], filename, { type: 'application/octet-stream' })
    onDddReady(file)
  }

  return (
    <div className="tw-w-full tw-min-w-[320px] tw-space-y-4">
      {/* Header */}
      <div>
        <h2 className="tw-text-base tw-font-semibold tw-text-slate-900">{t('importModule.webcard-title')}</h2>
        <p className="tw-mt-1 tw-text-sm tw-text-slate-500">
          {t('importModule.webcard-subtitle')}
        </p>
      </div>

      {/* Status badge */}
      <StatusBadge status={status} t={t} />

      {/* No extension – download installer */}
      {status === 'no-extension' && <NoExtensionPanel t={t} />}

      {/* Progress bar – shown while reading and when done */}
      {(status === 'reading' || status === 'done') && (
        <ReadingProgressBar progress={progress} t={t} />
      )}

      {/* Action buttons */}
      <div className="tw-flex tw-gap-3">
        {!isDone && (
          <button
            type="button"
            onClick={handleRead}
            disabled={!canRead || isReading}
            className={[
              'tw-flex-1 tw-rounded-lg tw-py-2.5 tw-text-sm tw-font-medium tw-transition',
              canRead && !isReading
                ? 'tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700'
                : 'tw-cursor-not-allowed tw-bg-slate-200 tw-text-slate-400',
            ].join(' ')}
          >
            {isReading ? t('importModule.webcard-reading') : t('importModule.webcard-read-card')}
          </button>
        )}

        {isDone && (
          <button
            type="button"
            onClick={handleImport}
            className="tw-flex-1 tw-rounded-lg tw-bg-green-600 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-transition hover:tw-bg-green-700"
          >
            {t('importModule.import-data')} →
          </button>
        )}
      </div>
    </div>
  )
}
